import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import useCountdown from '@bradgarropy/use-countdown'
import { useGet, usePost } from 'api'
import { load } from 'recaptcha-v3'

import { SmsProvider } from 'meta/pages/v4/otp'
import { ApiEndpoint } from 'controller/endpoint'
import {
  OTP_RESEND_TIMEOUT_MILLISECONDS,
  RECAPTCHA_ACTION,
  RECAPTCHA_HOST,
  RECAPTCHA_OTP_ACTION,
  RECAPTCHA_TOKEN,
} from 'utils/constants/auth'

import { useGoogleRecaptchaKey } from 'hooks/useEnv'
import { useOnUpdate } from 'hooks/useOnUpdate'

export const useResendOtp = (
  action: string,
  authToken: string,
  otpTokenInitial: string,
  phone: number,
  phonePrefix: string,
  S2SToken: string,
  useRecaptcha?: boolean
) => {
  const { error, fetch, response } = usePost()

  // this is needed in order to fetch sms provider info separately from the main form submission
  const { fetch: fetchSms, error: errorSms, response: responseSms, status: statusSms } = useGet()

  const [otpToken, setOtpToken] = useState<string>(otpTokenInitial)

  const [canResend, setCanResend] = useState(false)

  const googleRecaptchaKey = useGoogleRecaptchaKey()
  const bodyRef = useRef<object>(null)
  const recaptchaRef = useRef(null)

  const [smsServiceProvider, setSmsServiceProvider] = useState<SmsProvider | null>(null)

  const enableResend = () => {
    setTimeout(() => {
      setCanResend(true)
    }, OTP_RESEND_TIMEOUT_MILLISECONDS)
  }

  const { formatted: timeLeft, reset } = useCountdown({
    minutes: OTP_RESEND_TIMEOUT_MILLISECONDS / 60000,
    autoStart: true,
    format: 'mm:ss',
  })

  const resend = async () => {
    await fetchSms({
      urlResource: ApiEndpoint.v4.otp.flow(phonePrefix),
      useV4: true,
    })
  }

  useEffect(() => {
    const doResendOtp = async () => {
      setCanResend(false)

      if (useRecaptcha) {
        //@ts-expect-error recaptchaRef might be null
        const token = await recaptchaRef.current.execute(RECAPTCHA_OTP_ACTION)
        //@ts-expect-error bodyRef might not be defined
        bodyRef.current = {
          ...bodyRef.current,
          '_csrfToken': '',
          [RECAPTCHA_TOKEN]: token,
          [RECAPTCHA_ACTION]: RECAPTCHA_OTP_ACTION,
          [RECAPTCHA_HOST]: `${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`,
        }
      }

      await fetch({
        authToken,
        urlResource: ApiEndpoint.users.otp(),
        body: {
          phone,
          phonePrefix,
          backend_route: action,
          ...(useRecaptcha
            ? {
                ...bodyRef.current,
              }
            : {}),
        },
        extraHeaders: {
          'Tsms-S2S-Token': S2SToken,
        },
      })
      enableResend()
      reset()
    }

    if (smsServiceProvider) {
      doResendOtp()
    }
  }, [smsServiceProvider])

  useLayoutEffect(() => {
    enableResend()
  }, [])

  useOnUpdate(() => {
    if (response && response.data) {
      setOtpToken(response.data.otpToken)
    }
  }, [response])

  useEffect(() => {
    if (useRecaptcha && googleRecaptchaKey) {
      ;(async () => {
        //@ts-expect-error recaptchaRef might be null
        recaptchaRef.current = await load(googleRecaptchaKey, {
          useEnterprise: true,
        })
        //@ts-expect-error recaptchaRef might be null
        if (isMobile) recaptchaRef.current.hideBadge()
      })()
    }
  }, [googleRecaptchaKey, useRecaptcha])

  useOnUpdate(() => {
    if (errorSms || statusSms === 404) {
      setSmsServiceProvider(SmsProvider.SMS_HOSTING)
    }

    if (responseSms) {
      setSmsServiceProvider(responseSms.data?.provider)
    }
  }, [errorSms, responseSms, statusSms])

  return {
    canResend,
    error,
    otpToken,
    resend,
    timeLeft,
  }
}
